import { useEffect, useRef, useState, type RefObject } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchContact,
  fetchConversation,
  fetchConversationByConnectContact,
} from '../api/endpoints.js';
import {
  initCcp,
  isCcpConfigured,
  openCcpSignInWindow,
  subscribeToAgent,
  subscribeToContacts,
  subscribeToIncoming,
  type ActiveContactInfo,
  type IncomingContact,
} from '../connect/ccp.js';
import { primeRingtone, requestRingtone, releaseRingtone } from '../connect/ringtone.js';
import { PageHeader } from '../components/PageHeader.js';
import { SellerContextCard, type SellerContext } from '../components/SellerContextCard.js';
import { ConversationPanel } from '../components/ConversationPanel.js';
import { CallPrepCard } from '../components/CallPrepCard.js';
import { EmptyState } from '../components/States.js';
import { IncomingContactAlert } from '../components/IncomingContactAlert.js';

// NOTE: server-push (SSE) incoming-chat alerting now lives app-wide in
// <AgentNotifications /> (mounted in Layout) so agents are alerted on any page. This
// page keeps only the amazon-connect-streams path (in-app accept/reject), which is
// dormant unless the CCP is embeddable.

function CcpPanel({
  containerRef,
  configured,
  agentState,
  softphoneOpen,
  onOpenSoftphone,
}: {
  containerRef: RefObject<HTMLDivElement | null>;
  configured: boolean;
  agentState: string | null;
  softphoneOpen: boolean;
  onOpenSoftphone: () => void;
}) {
  // "Connected" if streams reports an agent (same-domain only) OR the agent has the
  // first-party softphone window open (the supported cross-site path).
  const connected = Boolean(agentState) || softphoneOpen;
  const badge = agentState ?? (softphoneOpen ? 'Signed in' : null);
  return (
    <div className="ccp-pane">
      <div className="ccp-pane__header">
        <span>Contact Control Panel</span>
        {badge && <span className="badge badge--agent">{badge}</span>}
      </div>
      {configured ? (
        <>
          <div className="ccp-signin">
            <button
              type="button"
              className={`btn btn--sm ${connected ? 'btn--secondary' : 'btn--primary'}`}
              onClick={onOpenSoftphone}
            >
              {connected ? 'Reopen softphone window ↗' : 'Open Amazon Connect softphone ↗'}
            </button>
            <span className="muted">
              {connected
                ? 'Signed in to Amazon Connect. Keep the softphone window open to take calls.'
                : 'Signs in via SSO and opens the softphone in its own window — keep it open to take calls.'}
            </span>
          </div>
          {/* Amazon Connect serves the CCP with `frame-ancestors 'none'`, so it cannot be
              embedded in another site's page (verified). The softphone therefore runs in its
              own first-party window (button above). This hidden container only lets
              amazon-connect-streams initialize for event wiring; it intentionally renders
              nothing visible. */}
          <div className="ccp-host ccp-host--hidden" ref={containerRef} />
          <p className="muted ccp-hint">
            Amazon Connect doesn’t allow embedding its softphone in another site, so it opens in
            the window above — keep that window open while you’re taking calls.
          </p>
        </>
      ) : (
        <div className="ccp-placeholder">
          <div className="ccp-placeholder__icon">☎</div>
          <h3>Amazon Connect not configured</h3>
          <p>
            Set <code>VITE_CONNECT_CCP_URL</code> to embed the softphone here. The rest of the
            workspace remains fully usable without it.
          </p>
        </div>
      )}
    </div>
  );
}

export function WorkspacePage() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ccpWindowRef = useRef<Window | null>(null);
  const [active, setActive] = useState<ActiveContactInfo | null>(null);
  const [incoming, setIncoming] = useState<IncomingContact | null>(null);
  const [acting, setActing] = useState<boolean>(false);
  const [agentState, setAgentState] = useState<string | null>(null);
  const [softphoneOpen, setSoftphoneOpen] = useState<boolean>(false);
  const configured = isCcpConfigured();

  // The CCP runs in a first-party window (it can't be iframed — Connect sends
  // frame-ancestors 'none'). Track whether that window is open so the workspace can
  // reflect the signed-in state instead of showing a dead "not logged in" panel.
  const openSoftphone = (): void => {
    // Unlock the WebAudio ringtone under the browser autoplay policy while we have a
    // user gesture, so the alert can ring later when a contact is offered.
    primeRingtone();
    const w = openCcpSignInWindow();
    if (w) {
      ccpWindowRef.current = w;
      setSoftphoneOpen(true);
      w.focus();
    }
  };
  useEffect(() => {
    if (!softphoneOpen) return;
    const t = setInterval(() => {
      if (ccpWindowRef.current?.closed) {
        setSoftphoneOpen(false);
        ccpWindowRef.current = null;
      }
    }, 2000);
    return () => clearInterval(t);
  }, [softphoneOpen]);

  // Initialize CCP once the host div is mounted, then subscribe — streams is
  // lazy-loaded (async) so subscriptions must wait until it's ready. Env-gated,
  // never throws.
  useEffect(() => {
    if (!configured || !containerRef.current) return;
    let cancelled = false;
    let offContact = (): void => {};
    let offAgent = (): void => {};
    let offIncoming = (): void => {};
    void initCcp(containerRef.current).then((ok) => {
      if (cancelled || !ok) return;
      offContact = subscribeToContacts(setActive);
      offAgent = subscribeToAgent((_, state) => setAgentState(state));
      offIncoming = subscribeToIncoming(setIncoming);
    });
    return () => {
      cancelled = true;
      offContact();
      offAgent();
      offIncoming();
    };
  }, [configured]);

  // Ring while a contact is being offered via streams (dormant unless the CCP is
  // embeddable). The SSE path's ringtone is owned by <AgentNotifications /> app-wide.
  useEffect(() => {
    if (incoming) requestRingtone('streams');
    else releaseRingtone('streams');
  }, [incoming]);
  useEffect(() => () => releaseRingtone('streams'), []);

  const focusSoftphone = (): void => {
    const w = ccpWindowRef.current;
    if (w && !w.closed) w.focus();
    else openSoftphone();
  };

  // Streams path (only when the CCP is embedded and firing events): true in-app
  // accept/reject driving Connect, with native requeue on reject.
  const acceptIncoming = (): void => {
    if (!incoming || acting) return;
    setActing(true);
    incoming
      .accept()
      .then(() => {
        setIncoming(null);
        focusSoftphone();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[workspace] failed to accept contact', err);
      })
      .finally(() => setActing(false));
  };

  const rejectIncoming = (): void => {
    if (!incoming || acting) return;
    setActing(true);
    incoming
      .reject()
      .then(() => setIncoming(null))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[workspace] failed to reject contact', err);
      })
      .finally(() => setActing(false));
  };

  const attrs = active?.attributes ?? {};
  const crmContactId = attrs.crm_contact_id;
  const crmConversationId = attrs.crm_conversation_id;

  const contactQ = useQuery({
    queryKey: ['contact', crmContactId],
    queryFn: () => fetchContact(crmContactId as string),
    enabled: Boolean(crmContactId),
  });

  // Chat/outbound the CRM initiated carry crm_conversation_id directly.
  const conversationQ = useQuery({
    queryKey: ['conversation', crmConversationId],
    queryFn: () => fetchConversation(crmConversationId as string),
    enabled: Boolean(crmConversationId),
  });

  // Inbound voice has no crm_conversation_id attribute — the worker creates the
  // row keyed on the Connect contact id a few seconds after the call starts.
  // Resolve by connect_contact_id and poll until it exists.
  const byContactQ = useQuery({
    queryKey: ['conversation-by-contact', active?.contactId],
    queryFn: () => fetchConversationByConnectContact(active!.contactId),
    enabled: Boolean(active?.contactId) && !crmConversationId,
    retry: false,
    refetchInterval: (q) => (q.state.data ? false : 4000),
  });

  const linkedConversationId = crmConversationId ?? byContactQ.data?.id;
  const linkedDisposition = crmConversationId
    ? conversationQ.data?.disposition
    : byContactQ.data?.disposition;
  const linkedStatus = crmConversationId
    ? conversationQ.data?.status
    : byContactQ.data?.status;
  // Contact for the prep card: the CCP attribute wins; else whatever the CRM row linked.
  const linkedContactId =
    crmContactId ?? conversationQ.data?.contact_id ?? byContactQ.data?.contact_id ?? null;

  const sellerContext: SellerContext = {
    sellerId: attrs.seller_id,
    brandName: attrs.brand_id,
    sourceDomain: attrs.source_domain,
    sourcePhone: attrs.source_phone_number,
    channel: attrs.channel,
    direction: attrs.direction,
  };

  return (
    <div className="page workspace">
      <PageHeader
        title="Agent Workspace"
        subtitle={
          active
            ? `Active contact ${active.contactId} · ${active.status}`
            : 'No active contact. Incoming contacts will appear here.'
        }
      />

      {incoming && (
        <IncomingContactAlert
          incoming={incoming}
          busy={acting}
          onAccept={acceptIncoming}
          onReject={rejectIncoming}
        />
      )}

      <div className="workspace__grid">
        <CcpPanel
          containerRef={containerRef}
          configured={configured}
          agentState={agentState}
          softphoneOpen={softphoneOpen}
          onOpenSoftphone={openSoftphone}
        />

        <div className="workspace__detail">
          <SellerContextCard
            context={sellerContext}
            contact={contactQ.data ?? null}
          />

          {linkedContactId && <CallPrepCard contactId={linkedContactId} compact />}

          {linkedConversationId ? (
            <ConversationPanel
              conversationId={linkedConversationId}
              currentDisposition={linkedDisposition}
              conversationStatus={linkedStatus}
              compact
            />
          ) : (
            <EmptyState
              title="No conversation linked"
              message={
                active
                  ? 'Linking this contact to its CRM conversation… (created moments after the call connects).'
                  : 'Accept a contact to load its customer card, timeline, notes, and disposition.'
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
